import { Injectable, computed, signal } from '@angular/core';
import { DeliveryDestinations } from 'customers';
import { CustomerConfigurationCustomer, isManualCustomer } from '../interfaces/company-search-result.model';

// Il nuovo destino resta nella bozza: non ha ancora un ID assegnato dal backend.
export type NewDeliveryDestination = Omit<DeliveryDestinations, 'id' | 'principale'>;
export type DestinationDetailsDraft = Pick<DeliveryDestinations, 'riferimento' | 'nota'>;

export interface CustomerConfigurationContact {
  nominativo: string;
  ruolo: string;
  telefono: string;
  email: string;
  note: string;
}

export interface CustomerConfigurationBanking {
  paymentType: string | null;
  abiDescription: string;
  cabDescription: string;
  sdiCode: string;
  vatExempt: boolean;
}

export interface CustomerConfigurationOrderNote {
  title: string;
  text: string;
  date: string;
  addToAuthorization: boolean;
}

export interface CustomerConfigurationState {
  customer: CustomerConfigurationCustomer | null;
  destination: DeliveryDestinations | NewDeliveryDestination | null;
  newDestination: boolean;
  oilZone: string | null;
  nonOilZone: string | null;
  contact: CustomerConfigurationContact;
  banking: CustomerConfigurationBanking;
  orderNote: CustomerConfigurationOrderNote | null;
  gdprDeferred: boolean;
  useManualAddressAsDestination: boolean;
}

// Bozza del nuovo flusso cliente: separata dalla proposta finché la configurazione non è conclusa.
@Injectable({ providedIn: 'root' })
export class CustomerConfigurationService {
  readonly customer = signal<CustomerConfigurationCustomer | null>(null);
  readonly useManualAddressAsDestination = signal(false);
  readonly proposalId = signal<string | null>(null);
  readonly destinations = signal<DeliveryDestinations[]>([]);
  readonly destinationsLoaded = signal(false);
  readonly selectedDestinationId = signal<string | null>(null);
  readonly newDestination = signal<NewDeliveryDestination | null>(null);
  readonly oilZone = signal<string | null>(null);
  readonly nonOilZone = signal<string | null>(null);
  readonly contact = signal<CustomerConfigurationContact>({ nominativo: '', ruolo: '', telefono: '', email: '', note: '' });
  readonly banking = signal<CustomerConfigurationBanking>({ paymentType: null, abiDescription: '', cabDescription: '', sdiCode: '', vatExempt: false });
  // Il file resta solo in memoria fino alla futura API: non viene copiato nello state di navigazione.
  readonly bankingDocument = signal<File | null>(null);
  readonly orderNote = signal<CustomerConfigurationOrderNote | null>(null);
  readonly gdprDocument = signal<File | null>(null);
  readonly gdprDeferred = signal(false);
  private readonly destinationDetails = signal<Record<string, DestinationDetailsDraft>>({});
  readonly selectedDestination = computed(() => {
    const destination = this.destinations().find(destination => destination.id === this.selectedDestinationId());
    return destination ? { ...destination, ...this.destinationDetails()[destination.id] } : null;
  });
  readonly destinationForConfiguration = computed(() => this.newDestination() ?? this.selectedDestination());

  begin(customer: CustomerConfigurationCustomer, proposalId: string | null): void {
    this.customer.set(customer);
    this.proposalId.set(proposalId);
    this.destinations.set(customer.destini ?? []);
    this.destinationsLoaded.set(isManualCustomer(customer) || customer.destini != null);
    this.useManualAddressAsDestination.set(false);
    this.selectedDestinationId.set(null);
    this.newDestination.set(null);
    this.destinationDetails.set({});
    this.oilZone.set(null);
    this.nonOilZone.set(null);
    this.contact.set({ nominativo: '', ruolo: '', telefono: '', email: '', note: '' });
    this.banking.set({ paymentType: null, abiDescription: '', cabDescription: '', sdiCode: '', vatExempt: false });
    this.bankingDocument.set(null);
    this.orderNote.set(null);
    this.gdprDocument.set(null);
    this.gdprDeferred.set(false);
  }

  setDestinations(destinations: DeliveryDestinations[]): void {
    this.destinations.set(destinations);
    this.destinationsLoaded.set(true);
    if (!destinations.some(destination => destination.id === this.selectedDestinationId())) {
      this.selectedDestinationId.set(null);
    }
  }

  setNewDestination(destination: NewDeliveryDestination): void {
    this.newDestination.set(destination);
    this.selectedDestinationId.set(null);
  }

  setDestinationDetails(id: string, details: DestinationDetailsDraft): void {
    this.destinationDetails.update(current => ({ ...current, [id]: details }));
  }

  getFlowState(): CustomerConfigurationState {
    return {
      customer: this.customer(),
      destination: this.destinationForConfiguration(),
      newDestination: !!this.newDestination(),
      oilZone: this.oilZone(),
      nonOilZone: this.nonOilZone(),
      contact: { ...this.contact() },
      banking: { ...this.banking() },
      orderNote: this.orderNote() ? { ...this.orderNote()! } : null,
      gdprDeferred: this.gdprDeferred(),
      useManualAddressAsDestination: this.useManualAddressAsDestination(),
    };
  }

  restoreFlowState(state: Partial<CustomerConfigurationState> | null, proposalId: string | null): void {
    if (!state?.customer) {
      return;
    }
    const customer = state.customer;
    if (isManualCustomer(customer)
      ? typeof customer.draftId !== 'string'
      : !customer.cribis || typeof customer.idBusinessPartner !== 'string') {
      return;
    }
    const current = this.customer();
    const sameCustomer = current && (isManualCustomer(customer)
      ? isManualCustomer(current) && current.draftId === customer.draftId
      : !isManualCustomer(current) && current.idBusinessPartner === customer.idBusinessPartner);
    if (this.proposalId() === proposalId && sameCustomer && (!state?.destination || this.destinationForConfiguration())) {
      return;
    }
    this.begin(customer, proposalId);
    if (state?.destination && state.newDestination) {
      this.setNewDestination(state.destination);
    } else if (state?.destination && 'id' in state.destination) {
      this.setDestinations([state.destination]);
      this.selectedDestinationId.set(state.destination.id);
    }
    this.oilZone.set(state.oilZone ?? null);
    this.nonOilZone.set(state.nonOilZone ?? null);
    if (state.contact) {
      this.contact.set({ ...state.contact });
    }
    if (state.banking) {
      this.banking.set({ ...state.banking });
    }
    if (state.orderNote) {
      this.orderNote.set({ ...state.orderNote });
    }
    this.gdprDeferred.set(state.gdprDeferred ?? false);
    this.useManualAddressAsDestination.set(state.useManualAddressAsDestination ?? false);
  }
}
